import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    ChartLineUp,
    DownloadSimple,
    UserPlus,
    CalendarPlus,
    ArrowsCounterClockwise,
    Handshake,
    Trophy,
    Star,
    UserCheck,
    ArrowUpRight,
    ArrowDownRight,
    Minus,
} from "@phosphor-icons/react";

const PERIODS = [
    { id: "day", label: "Day", sub: "hourly" },
    { id: "week", label: "Week", sub: "Mon–Sun" },
    { id: "month", label: "Month", sub: "weekly" },
    { id: "quarter", label: "Quarter", sub: "last 3 mo" },
    { id: "year", label: "Year", sub: "last 12 mo" },
];

// Small helper — returns { label, cls, Icon } describing the delta vs prior period.
function deltaChip(current, previous) {
    const c = Number(current) || 0;
    const p = Number(previous) || 0;
    if (p === 0 && c === 0) return { label: "0%", cls: "text-[#A3A3A3]", Icon: Minus };
    if (p === 0) return { label: "New", cls: "text-[#059669]", Icon: ArrowUpRight };
    const pct = Math.round(((c - p) / p) * 100);
    if (pct === 0) return { label: "0%", cls: "text-[#A3A3A3]", Icon: Minus };
    const up = pct > 0;
    return {
        label: `${up ? "+" : ""}${pct}%`,
        cls: up ? "text-[#059669]" : "text-[#DC2626]",
        Icon: up ? ArrowUpRight : ArrowDownRight,
    };
}

const PREV_LABEL = {
    day: "vs yesterday",
    week: "vs last week",
    month: "vs last month",
    quarter: "vs prev 3 mo",
    year: "vs last year",
};

// V1.5.8.29 — the top metrics strip and the bucketed table share this
// list. `registration_done` intentionally lives ONLY in TABLE_METRICS
// below (not in the strip) — the strip's "REG. DONE" card looked like
// a duplicate of the "REGISTRATION DONE" portfolio card below the strip.
const METRICS = [
    { key: "new_candidate_tag",         label: "New tagged",     icon: UserPlus,               color: "text-cobalt" },
    { key: "new_candidate_lineup",      label: "New lineup",     icon: CalendarPlus,           color: "text-[#7E22CE]" },
    { key: "previous_candidate_lineup", label: "Re-lineup",      icon: ArrowsCounterClockwise, color: "text-[#F59E0B]" },
    { key: "offer",                     label: "Offer",          icon: Handshake,              color: "text-[#0EA5E9]" },
    { key: "hired",                     label: "Hired",          icon: Trophy,                 color: "text-[#059669]" },
    { key: "testimonials_success",      label: "Testimonials",   icon: Star,                   color: "text-[#DC2626]" },
];

const TABLE_METRICS = [
    ...METRICS.slice(0, 5),
    { key: "registration_done",         label: "Reg. done",      icon: UserCheck,              color: "text-[#059669]" },
    METRICS[5], // Testimonials at the end
];

export default function PerformanceReport() {
    const { user } = useAuth();
    const [period, setPeriod] = useState("week");
    const [recruiterId, setRecruiterId] = useState("all");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const isAdminOrHM = user && (user.role === "admin" || user.role === "hiring_manager");

    const load = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams({ period });
        if (isAdminOrHM && recruiterId && recruiterId !== "all") {
            params.set("recruiter_id", recruiterId);
        }
        api.get(`/performance/report?${params.toString()}`)
            .then((r) => setData(r.data))
            .catch((e) => {
                logError("Performance report failed:", e);
                toast.error("Couldn't load performance report.");
            })
            .finally(() => setLoading(false));
    }, [period, recruiterId, isAdminOrHM]);

    useEffect(() => { load(); }, [load]);

    const downloadCsv = async () => {
        try {
            const params = new URLSearchParams({ period });
            if (isAdminOrHM && recruiterId && recruiterId !== "all") {
                params.set("recruiter_id", recruiterId);
            }
            const r = await api.get(`/performance/report.csv?${params.toString()}`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `performance-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Report downloaded — opens in Excel.");
        } catch (e) {
            toast.error("Couldn't download CSV.");
        }
    };

    const totals = data?.totals || {};
    const previousTotals = data?.previous_totals || {};
    const buckets = data?.buckets || [];
    const opts = data?.recruiter_options || [];
    const prevLabel = PREV_LABEL[period] || "vs prev";

    // Find max value per row so we can render tiny inline sparkbars.
    const maxByMetric = useMemo(() => {
        const m = {};
        for (const met of TABLE_METRICS) {
            m[met.key] = Math.max(1, ...buckets.map((b) => b[met.key] || 0));
        }
        return m;
    }, [buckets]);

    return (
        <div
            data-testid="performance-page"
            className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1400px] mx-auto"
        >
            {/* Header */}
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 inline-flex items-center gap-1.5">
                        <ChartLineUp size={12} weight="bold" /> Team pulse
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                        Performance.
                    </h1>
                    <p className="text-sm text-[#525252] mt-2">
                        {isAdminOrHM
                            ? "See how the team is tagging, lining up and closing."
                            : "Your last-week numbers, hour by hour."}
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="perf-export-csv"
                    onClick={downloadCsv}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] font-black uppercase tracking-wider bg-ink text-white hover:opacity-90"
                >
                    <DownloadSimple size={13} weight="bold" /> Export to Excel
                </button>
            </div>

            {/* Filters */}
            <div
                data-testid="perf-filters"
                className="flex flex-wrap items-center gap-3 mb-6"
            >
                {/* Period pills */}
                <div className="inline-flex bg-white border border-[#E5E5E5] p-1">
                    {PERIODS.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            data-testid={`perf-period-${p.id}`}
                            onClick={() => setPeriod(p.id)}
                            className={`px-3 py-2 text-[11px] font-black uppercase tracking-wider transition-colors ${
                                period === p.id
                                    ? "bg-ink text-white"
                                    : "text-[#525252] hover:text-ink"
                            }`}
                        >
                            <span className="block leading-tight">{p.label}</span>
                            <span className={`block text-[9px] tracking-normal font-medium ${
                                period === p.id ? "text-white/60" : "text-[#A3A3A3]"
                            }`}>{p.sub}</span>
                        </button>
                    ))}
                </div>

                {/* Recruiter dropdown (admin/HM only) */}
                {isAdminOrHM && (
                    <label className="inline-flex items-center gap-2 text-xs">
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252]">
                            Recruiter
                        </span>
                        <select
                            data-testid="perf-recruiter-select"
                            value={recruiterId}
                            onChange={(e) => setRecruiterId(e.target.value)}
                            className="border border-[#E5E5E5] px-3 py-2 text-xs bg-white min-w-[180px]"
                        >
                            <option value="all">Everyone</option>
                            {opts.map((o) => (
                                <option key={o.id} value={o.id}>
                                    {o.name} · {o.role}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
            </div>

            {/* Totals cards */}
            <div
                data-testid="perf-totals"
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4"
            >
                {METRICS.map(({ key, label, icon: Icon, color }) => {
                    const chip = deltaChip(totals[key], previousTotals[key]);
                    const DIcon = chip.Icon;
                    return (
                        <div
                            key={key}
                            data-testid={`perf-total-${key}`}
                            className="bg-white border border-[#E5E5E5] p-4"
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <Icon size={13} weight="bold" className={color} />
                                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252]">
                                    {label}
                                </p>
                            </div>
                            <p className="font-heading text-3xl font-black tracking-tighter text-ink">
                                {totals[key] || 0}
                            </p>
                            <div
                                data-testid={`perf-delta-${key}`}
                                className={`mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider ${chip.cls}`}
                                title={`Previous period: ${previousTotals[key] || 0}`}
                            >
                                <DIcon size={11} weight="bold" />
                                {chip.label}
                                <span className="text-[9px] font-medium normal-case tracking-normal text-[#A3A3A3] ml-0.5">
                                    {prevLabel}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* V1.5.8.26 — Portfolio-wide Registration KPIs (NOT bucketed).
                Shows the current book of candidates for the scope selected
                above and how many have claimed their portal account. */}
            {data?.registration && (
                <div
                    data-testid="perf-registration"
                    className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6"
                >
                    <div className="bg-white border border-[#E5E5E5] p-4" data-testid="perf-reg-done">
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252] mb-1">
                            Registration done
                        </p>
                        <p className="font-heading text-3xl font-black tracking-tighter text-ink">
                            {data.registration.done ?? 0}
                        </p>
                        <p className="text-[10px] text-[#A3A3A3] mt-1">
                            of {data.registration.total_owned ?? 0} candidates
                        </p>
                    </div>
                    <div className="bg-white border border-[#E5E5E5] p-4" data-testid="perf-reg-pct">
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252] mb-1">
                            Reg. done %
                        </p>
                        {data.registration.pct === null || data.registration.pct === undefined ? (
                            <p className="font-heading text-3xl font-black tracking-tighter text-[#D4D4D4]">—</p>
                        ) : (
                            <p className={`font-heading text-3xl font-black tracking-tighter ${
                                data.registration.pct >= 60 ? "text-[#059669]" :
                                data.registration.pct >= 30 ? "text-[#B45309]" :
                                                              "text-[#B91C1C]"
                            }`}>
                                {data.registration.pct}%
                            </p>
                        )}
                        <p className="text-[10px] text-[#A3A3A3] mt-1">
                            Portal-claim rate (portfolio-wide)
                        </p>
                    </div>
                    <div className="bg-white border border-[#E5E5E5] p-4" data-testid="perf-reg-pending">
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252] mb-1">
                            Registration pending
                        </p>
                        <p className="font-heading text-3xl font-black tracking-tighter text-ink">
                            {Math.max(0, (data.registration.total_owned ?? 0) - (data.registration.done ?? 0))}
                        </p>
                        <p className="text-[10px] text-[#A3A3A3] mt-1">
                            Nudge candidates to close the loop
                        </p>
                    </div>
                </div>
            )}

            {/* Table */}
            <div
                data-testid="perf-table-card"
                className="bg-white border border-[#E5E5E5] overflow-x-auto"
            >
                {loading && (
                    <div className="p-10 text-center text-sm text-[#525252]">Loading…</div>
                )}
                {!loading && buckets.length > 0 && (
                    <table className="w-full text-sm">
                        <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                            <tr>
                                <th className="text-left p-3 text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                    {period === "day" ? "Hour" :
                                     period === "week" ? "Day" :
                                     period === "month" ? "Week" : "Month"}
                                </th>
                                {TABLE_METRICS.map((m) => (
                                    <th
                                        key={m.key}
                                        className="text-right p-3 text-[10px] font-black uppercase tracking-wider text-[#525252]"
                                    >
                                        {m.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {buckets.map((b) => (
                                <tr
                                    key={b.key}
                                    data-testid={`perf-row-${b.key}`}
                                    className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]"
                                >
                                    <td className="p-3 font-bold text-ink whitespace-nowrap">
                                        {b.label}
                                    </td>
                                    {TABLE_METRICS.map((m) => {
                                        const v = b[m.key] || 0;
                                        const max = maxByMetric[m.key] || 1;
                                        return (
                                            <td
                                                key={m.key}
                                                className="p-3 text-right"
                                                data-testid={`perf-cell-${b.key}-${m.key}`}
                                            >
                                                <div className="inline-flex items-center gap-2 min-w-[70px] justify-end">
                                                    <div className="w-10 h-1 bg-[#F5F5F5] relative">
                                                        <div
                                                            className={`absolute inset-y-0 left-0 ${m.color.replace("text-", "bg-")}`}
                                                            style={{ width: `${(v / max) * 100}%` }}
                                                        />
                                                    </div>
                                                    <span className={`font-heading font-black tabular-nums ${v > 0 ? "text-ink" : "text-[#D4D4D4]"}`}>
                                                        {v}
                                                    </span>
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                            {/* Totals row */}
                            <tr
                                data-testid="perf-totals-row"
                                className="bg-[#FAFAFA] border-t-2 border-[#E5E5E5]"
                            >
                                <td className="p-3 text-[10px] font-black uppercase tracking-wider text-ink">
                                    Total
                                </td>
                                {TABLE_METRICS.map((m) => (
                                    <td key={m.key} className="p-3 text-right">
                                        <span className="font-heading font-black text-lg text-ink tabular-nums">
                                            {totals[m.key] || 0}
                                        </span>
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                )}
                {!loading && buckets.length === 0 && (
                    <div className="p-10 text-center text-sm text-[#525252]">
                        No data for this period.
                    </div>
                )}
            </div>
        </div>
    );
}
